import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Home, ZoomIn, ZoomOut, Move, Info, X, MousePointer } from 'lucide-react';
import { LazyLoadImage } from 'react-lazy-load-image-component';

const MAP_WIDTH = 2178;
const MAP_HEIGHT = 1628;
const MAX_SCALE = 4;

const HideoutMap = ({
    hideoutModules,
    hideoutLevels,
    selectedModuleId,
    setSelectedModuleId,
    canUpgrade,
    t,
    modulePositions
}) => {
    // Harita Durumları (Artık sadece bu dosyanın içinde!)
    const [scale, setScale] = useState(0.5);
    const [minScale, setMinScale] = useState(0.5);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [showHelp, setShowHelp] = useState(false);

    const mapContainerRef = useRef(null);
    const isDragging = useRef(false);
    const lastMousePosition = useRef({ x: 0, y: 0 });

    const clampPosition = useCallback((x, y, s) => {
        if (!mapContainerRef.current) return { x, y };
        const container = mapContainerRef.current;
        const scaledWidth = MAP_WIDTH * s;
        const scaledHeight = MAP_HEIGHT * s;
        const cWidth = container.clientWidth;
        const cHeight = container.clientHeight;
        let newX = scaledWidth < cWidth ? (cWidth - scaledWidth) / 2 : Math.min(Math.max(x, cWidth - scaledWidth), 0);
        let newY = scaledHeight < cHeight ? (cHeight - scaledHeight) / 2 : Math.min(Math.max(y, cHeight - scaledHeight), 0);
        return { x: newX, y: newY };
    }, []);

    useEffect(() => {
        const handleResize = () => {
            if (mapContainerRef.current) {
                const { clientWidth, clientHeight } = mapContainerRef.current;
                const widthRatio = clientWidth / MAP_WIDTH;
                const heightRatio = clientHeight / MAP_HEIGHT;
                const newMinScale = Math.max(widthRatio, heightRatio);
                setMinScale(newMinScale);
                setScale(prev => Math.max(prev, newMinScale));
            }
        };
        const observer = new ResizeObserver(handleResize);
        if (mapContainerRef.current) observer.observe(mapContainerRef.current);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        setPosition(prev => clampPosition(prev.x, prev.y, scale));
    }, [scale, clampPosition]);

    useEffect(() => {
        const container = mapContainerRef.current;
        if (!container) return;
        const onWheel = (e) => {
            e.preventDefault();
            const delta = e.deltaY * -0.001;
            setScale(s => Math.min(Math.max(minScale, s + delta), MAX_SCALE));
        };
        container.addEventListener('wheel', onWheel, { passive: false });
        return () => container.removeEventListener('wheel', onWheel);
    }, [minScale]);

    const handleMouseDown = (e) => {
        isDragging.current = true;
        lastMousePosition.current = { x: e.clientX, y: e.clientY };
        if (mapContainerRef.current) mapContainerRef.current.style.cursor = 'grabbing';
    };

    const handleMouseMove = (e) => {
        if (!isDragging.current) return;
        const dx = e.clientX - lastMousePosition.current.x;
        const dy = e.clientY - lastMousePosition.current.y;
        setPosition(prev => clampPosition(prev.x + dx, prev.y + dy, scale));
        lastMousePosition.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseUp = () => {
        isDragging.current = false;
        if (mapContainerRef.current) mapContainerRef.current.style.cursor = 'grab';
    };

    const HelpOverlay = () => (
        <div className="absolute inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn" onClick={() => setShowHelp(false)}>
            <div className="bg-[#09090b] border border-zinc-800 rounded-sm p-6 max-w-lg w-full shadow-2xl relative" onClick={(e) => e.stopPropagation()}>
                <button onClick={() => setShowHelp(false)} className="absolute top-4 right-4 text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
                <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                    <Info className="w-6 h-6 text-emerald-500" /> {t('hideout_help_title') || 'Hideout Help'}
                </h2>
                <div className="space-y-6">
                    <div className="flex gap-4">
                        <div className="bg-zinc-800/50 p-2 rounded-md h-fit border border-zinc-700"><MousePointer className="w-5 h-5 text-emerald-400" /></div>
                        <div>
                            <h3 className="text-white font-bold text-sm">Interaction</h3>
                            <p className="text-zinc-400 text-xs leading-relaxed">Drag to pan the map. Scroll to zoom. Click stations to see details.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="flex-1 relative overflow-hidden bg-black cursor-grab active:cursor-grabbing"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            ref={mapContainerRef}>

            {showHelp && <HelpOverlay />}

            <div style={{
                transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                transformOrigin: '0 0',
                transition: isDragging.current ? 'none' : 'transform 0.1s ease-out',
                width: MAP_WIDTH,
                height: MAP_HEIGHT,
                aspectRatio: `${MAP_WIDTH}/${MAP_HEIGHT}`
            }} className="relative mx-auto">

                <div className="absolute inset-0 pointer-events-none z-0" style={{
                    backgroundColor: 'black',
                    backgroundImage: `linear-gradient(rgba(16, 185, 129, 0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(16, 185, 129, 0.08) 1px, transparent 1px)`,
                    backgroundSize: '40px 40px'
                }}></div>

                <img src="/Images/MapBase (1).svg" alt="Hideout Map" className="absolute inset-0 w-full h-full object-contain pointer-events-none z-10" style={{ opacity: 0.9 }} />

                <div className="absolute inset-0 z-20">
                    {hideoutModules.map(module => {
                        const pos = modulePositions[module.englishName] || { x: 50, y: 50 };
                        const transKey = `hideout_module_${(module.englishName || module.name).replace(/ /g, '_')}`;
                        const realLevel = hideoutLevels[module.id] || 0;
                        const isMaxed = realLevel >= module.levels.length;
                        const isSelected = selectedModuleId === module.id;
                        const canUpg = !isMaxed && canUpgrade(module, realLevel + 1, hideoutLevels);

                        let containerClass = "border-2 transition-all duration-300";
                        let iconColor = "text-zinc-400";
                        let glowStyle = {};

                        if (isMaxed) {
                            containerClass += " bg-yellow-600/30 border-yellow-400"; iconColor = "text-yellow-400"; glowStyle = { boxShadow: "0 0 20px rgba(250, 204, 21, 0.5)" };
                        } else if (canUpg) {
                            containerClass += " bg-emerald-600/30 border-emerald-400 animate-pulse"; iconColor = "text-emerald-300"; glowStyle = { boxShadow: "0 0 20px rgba(52, 211, 153, 0.5)" };
                        } else {
                            containerClass += " bg-zinc-900/60 border-zinc-700 opacity-60"; iconColor = "text-zinc-500";
                        }

                        if (isSelected) {
                            containerClass = "border-2 bg-zinc-700 scale-125 z-50 border-white shadow-2xl"; iconColor = "text-white"; glowStyle = { boxShadow: "0 0 30px rgba(255, 255, 255, 0.7)" };
                        }

                        const displayName = t(transKey) && t(transKey) !== transKey ? t(transKey) : module.name;

                        return (
                            <div key={module.id}
                                className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer z-30 group"
                                style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
                                onClick={(e) => { e.stopPropagation(); setSelectedModuleId(module.id); }}>
                                <div className={`w-14 h-14 clip-hexagon flex items-center justify-center ${containerClass}`} style={glowStyle}>
                                    {module.imageLink ? (
                                        <LazyLoadImage src={module.imageLink} className="w-8 h-8 object-contain opacity-90 group-hover:opacity-100" alt={module.name} effect="blur" />
                                    ) : (
                                        <Home className={`w-5 h-5 ${iconColor}`} />
                                    )}
                                </div>
                                <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                                    <span className="text-[10px] font-bold text-white bg-black/90 px-2 py-0.5 rounded-sm border border-zinc-800 whitespace-nowrap uppercase tracking-widest">{displayName}</span>
                                    <span className={`text-[9px] font-mono font-bold bg-black/80 px-1 mt-0.5 rounded-sm ${isMaxed ? 'text-yellow-500' : canUpg ? 'text-emerald-500' : 'text-zinc-500'}`}>L{realLevel}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="absolute bottom-6 left-6 flex flex-col gap-2 z-50" onMouseDown={(e) => e.stopPropagation()}>
                <button onClick={() => setScale(s => Math.min(Math.max(minScale, s + 0.5), MAX_SCALE))} className="p-3 bg-[#09090b] border border-zinc-800 text-zinc-400 hover:text-white hover:border-emerald-500 rounded-sm shadow-xl transition-all"><ZoomIn className="w-5 h-5" /></button>
                <button onClick={() => setScale(s => Math.min(Math.max(minScale, s - 0.5), MAX_SCALE))} className="p-3 bg-[#09090b] border border-zinc-800 text-zinc-400 hover:text-white hover:border-emerald-500 rounded-sm shadow-xl transition-all"><ZoomOut className="w-5 h-5" /></button>
                <button onClick={() => { setScale(minScale); setPosition({ x: 0, y: 0 }); }} className="p-3 bg-[#09090b] border border-zinc-800 text-zinc-400 hover:text-white hover:border-emerald-500 rounded-sm shadow-xl transition-all"><Move className="w-5 h-5" /></button>
                <button onClick={() => setShowHelp(true)} className="p-3 bg-[#09090b] border border-zinc-800 text-emerald-500 hover:text-white hover:border-emerald-500 rounded-sm shadow-xl transition-all mt-2"><Info className="w-5 h-5" /></button>
            </div>
        </div>
    );
};

export default HideoutMap;